import { User, LoginRequest, AuthResponse } from '@/types';
import { apiClient, API_ENDPOINTS, handleApiError } from './api';

export const authService = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      console.log('Attempting login with backend API for:', credentials.email);
      
      const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, {
        email: credentials.email,
        password: credentials.password
      });
      
      const { access_token, refresh_token, token_type, expires_in } = response.data;
      
      // Get user info after successful login
      const userResponse = await apiClient.get(API_ENDPOINTS.AUTH.ME, {
        headers: {
          Authorization: `Bearer ${access_token}`
        }
      });
      
      const user: User = {
        id: userResponse.data.id,
        email: userResponse.data.email,
        username: userResponse.data.email.split('@')[0], // Backend doesn't have username field
        full_name: userResponse.data.full_name,
        workspace_id: 'default', // Will be updated when workspaces are integrated
        workspace_name: 'Default Workspace',
        is_active: userResponse.data.is_active,
        is_superuser: userResponse.data.is_superuser,
        created_at: userResponse.data.created_at,
        updated_at: userResponse.data.updated_at
      };
      
      const authResponse: AuthResponse = {
        access_token,
        refresh_token,
        token_type,
        expires_in,
        user
      };
      
      // Store tokens and user info
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      console.log('Login successful:', user);
      return authResponse;
      
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async register(userData: {
    email: string;
    password: string;
    name: string;
    workspace_name?: string;
  }): Promise<AuthResponse> {
    try {
      console.log('Attempting registration with backend API for:', userData.email);
      
      // Register user
      const response = await apiClient.post(API_ENDPOINTS.AUTH.REGISTER, {
        email: userData.email,
        password: userData.password,
        full_name: userData.name,
        username: userData.email.split('@')[0] // Generate username from email
      });
      
      const registeredUser = response.data;
      
      // After registration, login to get tokens
      const loginResponse = await this.login({
        email: userData.email,
        password: userData.password
      });
      
      console.log('Registration successful:', registeredUser);
      return loginResponse;
      
    } catch (error: any) {
      console.error('Registration error:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async logout(): Promise<void> {
    try {
      console.log('Attempting logout with backend API');
      
      // Call backend logout endpoint
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
      
      // Clear stored data regardless of API response
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      
      console.log('Logout successful');
      
    } catch (error: any) {
      console.error('Logout error:', error);
      // Still clear local storage even if API call fails
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  },

  async getCurrentUser(): Promise<User> {
    try {
      console.log('Fetching current user from backend API');
      
      const response = await apiClient.get(API_ENDPOINTS.AUTH.ME);
      
      const user: User = {
        id: response.data.id,
        email: response.data.email,
        username: response.data.email.split('@')[0],
        full_name: response.data.full_name,
        workspace_id: 'default',
        workspace_name: 'Default Workspace',
        is_active: response.data.is_active,
        is_superuser: response.data.is_superuser,
        created_at: response.data.created_at,
        updated_at: response.data.updated_at
      };
      
      // Update stored user info
      localStorage.setItem('user', JSON.stringify(user));
      
      return user;
      
    } catch (error: any) {
      console.error('Get current user error:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async refreshToken(): Promise<AuthResponse> {
    try {
      console.log('Attempting token refresh with backend API');
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiClient.post(API_ENDPOINTS.AUTH.REFRESH, {
        refresh_token: refreshToken
      });
      
      const { access_token, refresh_token: newRefreshToken, token_type, expires_in } = response.data;
      
      // Get updated user info
      const userResponse = await apiClient.get(API_ENDPOINTS.AUTH.ME, {
        headers: {
          Authorization: `Bearer ${access_token}`
        }
      });
      
      const user: User = {
        id: userResponse.data.id,
        email: userResponse.data.email,
        username: userResponse.data.email.split('@')[0],
        full_name: userResponse.data.full_name,
        workspace_id: 'default',
        workspace_name: 'Default Workspace',
        is_active: userResponse.data.is_active,
        is_superuser: userResponse.data.is_superuser,
        created_at: userResponse.data.created_at,
        updated_at: userResponse.data.updated_at
      };
      
      const authResponse: AuthResponse = {
        access_token,
        refresh_token: newRefreshToken,
        token_type,
        expires_in,
        user
      };
      
      // Update stored tokens and user info
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', newRefreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      
      console.log('Token refresh successful');
      return authResponse;
      
    } catch (error: any) {
      console.error('Token refresh error:', error);
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  async verifyToken(): Promise<boolean> {
    try {
      console.log('Verifying token with backend API');
      
      const response = await apiClient.get(API_ENDPOINTS.AUTH.VERIFY_TOKEN);
      return response.data.valid === true;
      
    } catch (error: any) {
      console.error('Token verification error:', error);
      return false;
    }
  },

  // Helper methods
  getAuthToken(): string | null {
    return localStorage.getItem('access_token');
  },

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  },

  getStoredUser(): User | null {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  },

  setAuthToken(token: string): void {
    localStorage.setItem('access_token', token);
  },

  setRefreshToken(token: string): void {
    localStorage.setItem('refresh_token', token);
  },

  clearAuthData(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }
}; 